import {Box, HStack, Icon, Text} from 'platform/foundation';
import {cva} from 'platform/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

interface MobileMenuTriggerProps extends TestIdProps {
  label: string | Nullish;
  isOpen: boolean;
  isDisabled?: boolean;
}

const CHEVRON_ICON = 'navigation/arrow_down';

const triggerWrapperVariants = cva('w-full outline-none', {
  variants: {
    isDisabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
    isOpen: {
      true: '[&>div]:[box-shadow:var(--shadow-active)]',
      false: '',
    },
  },
  compoundVariants: [
    {
      isDisabled: false,
      isOpen: false,
      class:
        '[&:hover>div]:border-[var(--color-severity-informational)] [&:hover>div]:bg-[var(--color-white)] [&:hover>div]:[box-shadow:var(--shadow-active)] [&:focus-visible>div]:border-[var(--color-severity-informational)] [&:focus-visible>div]:bg-[var(--color-white)] [&:focus-visible>div]:[box-shadow:var(--shadow-active)]',
    },
  ],
});

const chevronWrapperVariants = cva('flex', {
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: 'rotate-0',
    },
  },
});

export function MobileMenuTrigger(props: MobileMenuTriggerProps) {
  return (
    <div
      aria-disabled={props.isDisabled}
      data-testid={suffixTestId('mobileMenuTrigger', props)}
      className={triggerWrapperVariants({
        isOpen: props.isOpen,
        isDisabled: Boolean(props.isDisabled),
      })}
    >
      <Box
        backgroundColor={getTriggerBackgroundColor(props.isOpen, props.isDisabled)}
        border="1px solid"
        borderColor={getTriggerBorderColor(props.isOpen, props.isDisabled)}
        borderRadius="small"
        height={11}
        padding={3}
        paddingRight={2}
        transition="border-color 150ms ease, box-shadow 150ms ease"
        width="100%"
      >
        <HStack justify="space-between" align="center" width="100%" height="100%">
          <Text
            alternative
            data-testid={suffixTestId('mobileMenuTriggerLabel', props)}
            size="small"
          >
            {props.label}
          </Text>

          <div className={chevronWrapperVariants({isOpen: props.isOpen})}>
            <Icon
              data-testid={suffixTestId('mobileMenuTriggerIcon', props)}
              size={4}
              value={CHEVRON_ICON}
            />
          </div>
        </HStack>
      </Box>
    </div>
  );
}

function getTriggerBackgroundColor(isOpen: boolean, isDisabled?: boolean) {
  if (isDisabled) {
    return 'palettes.neutral.10.100';
  }

  if (isOpen) {
    return 'general.white';
  }

  return 'palettes.neutral.30.100';
}

function getTriggerBorderColor(isOpen: boolean, isDisabled?: boolean) {
  if (isDisabled) {
    return 'palettes.neutral.40.100';
  }

  if (isOpen) {
    return 'severity.informational';
  }

  return 'palettes.neutral.30.100';
}
